import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { GemCraftPanel } from "../components/GemCraftPanel";
import { useBlacksmithStore } from "../store/useBlacksmithStore";
import { useGameStore } from "../store/useGameStore";
import type { MaterialStack, PlayerItem } from "../types";
import type { BlacksmithConfig } from "../api/blacksmith";
import { gemAutoCraftEntitlements } from "../utils/premium";

const TABS = [
  { id: "gems" as const, label: "Gems" },
  { id: "craft" as const, label: "Armor" },
  { id: "legendary" as const, label: "Legendary" },
];

const ARMOR_SLOTS = [
  { id: "HELM", label: "Helm" },
  { id: "HANDS", label: "Gauntlets" },
  { id: "BODY", label: "Plate" },
  { id: "WEAPON", label: "Weapon" },
];

export function BlacksmithScreen() {
  const { balance, hero, user, setScreen, refreshHero } = useGameStore();
  const {
    tab,
    config,
    gems,
    shards,
    legendaryStones,
    epicItems,
    loading,
    actionLoading,
    message,
    error,
    setTab,
    refresh,
    synthesizeGem,
    sellGem,
    exchangeGem,
    autoCraftGems,
    autoCraftGemsFull,
    previewAutoCraftFull,
    craftArmor,
    legendaryUpgrade,
  } = useBlacksmithStore();

  const gemCraftUnlock = gemAutoCraftEntitlements(hero?.premium ?? null);

  const [craftSlot, setCraftSlot] = useState("HELM");
  const [craftLevel, setCraftLevel] = useState(40);
  const [craftBaseItemId, setCraftBaseItemId] = useState<number | null>(null);
  const [upgradeItemId, setUpgradeItemId] = useState<number | null>(null);

  const craftPrevLevel = config?.previous_level?.[String(craftLevel)];
  const craftBaseCandidates = useMemo(() => {
    if (craftPrevLevel == null) return [];
    const prev = Number(craftPrevLevel);
    return epicItems.filter(
      (i) =>
        !i.is_equipped &&
        i.template.slot === craftSlot &&
        i.template.item_level === prev &&
        i.template.rarity === "EPIC",
    );
  }, [epicItems, craftSlot, craftPrevLevel]);

  useEffect(() => {
    if (craftBaseCandidates.length === 0) {
      setCraftBaseItemId(null);
      return;
    }
    if (craftBaseItemId == null || !craftBaseCandidates.some((i) => i.id === craftBaseItemId)) {
      setCraftBaseItemId(craftBaseCandidates[0].id);
    }
  }, [craftBaseCandidates, craftBaseItemId]);

  useEffect(() => {
    void refresh();
    void refreshHero();
  }, [refresh, refreshHero]);

  useEffect(() => {
    if (config?.craft_levels?.length && !config.craft_levels.includes(craftLevel)) {
      setCraftLevel(config.craft_levels[0]);
    }
  }, [config, craftLevel]);

  useEffect(() => {
    if (epicItems.length > 0 && upgradeItemId === null) {
      setUpgradeItemId(epicItems[0].id);
    }
    if (epicItems.length === 0) {
      setUpgradeItemId(null);
    }
  }, [epicItems, upgradeItemId]);

  return (
    <div className="p-4 space-y-4 pb-8">
      <button type="button" onClick={() => setScreen("main")} className="text-sm text-gray-400 hover:text-white">
        ← Back to Home
      </button>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold font-game text-gold-400">🔨 Blacksmith</h2>
        <p className="text-gray-400 text-sm">Gem synthesis, armor crafting, legendary upgrades</p>
      </div>

      <div className="flex gap-1 bg-kingdom-dark rounded-xl p-1 border border-kingdom-mid">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={clsx(
              "flex-1 py-2 rounded-lg text-xs font-bold transition-colors",
              tab === t.id ? "bg-kingdom-glow text-black" : "text-gray-400 hover:text-white",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">{error}</div>
      )}
      {message && (
        <div className="bg-emerald-900/30 border border-emerald-500 rounded-xl p-3 text-emerald-300 text-sm">
          {message}
        </div>
      )}

      {loading && tab === "gems" && gems.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-6">Loading...</p>
      )}

      {tab === "gems" && (
        <GemCraftPanel
          gems={gems}
          config={config}
          gold={balance?.gold ?? 0}
          crypto={balance?.crypto ?? 0}
          heroClass={hero?.hero_class}
          userId={user?.id}
          actionLoading={actionLoading}
          autoCraftBasicUnlocked={gemCraftUnlock.basic}
          autoCraftFullUnlocked={gemCraftUnlock.full}
          onNeedPremium={() => setScreen("premium")}
          onSynthesize={synthesizeGem}
          onAutoCraft={autoCraftGems}
          onAutoCraftFull={autoCraftGemsFull}
          onPreviewAutoCraftFull={previewAutoCraftFull}
          onSellGem={sellGem}
          onExchangeGem={exchangeGem}
        />
      )}

      {tab === "craft" && (
        <ArmorCraftPanel
          config={config}
          heroLevel={hero?.level ?? 0}
          shards={shards}
          crypto={balance?.crypto ?? 0}
          gold={balance?.gold ?? 0}
          craftSlot={craftSlot}
          craftLevel={craftLevel}
          baseItemId={craftBaseItemId}
          baseCandidates={craftBaseCandidates}
          actionLoading={actionLoading}
          onSlotChange={setCraftSlot}
          onLevelChange={setCraftLevel}
          onBaseItemChange={setCraftBaseItemId}
          onCraft={() =>
            void craftArmor(
              craftSlot,
              craftLevel,
              craftPrevLevel != null ? craftBaseItemId ?? undefined : undefined,
            )
          }
        />
      )}

      {tab === "legendary" && (
        <LegendaryUpgradePanel
          config={config}
          epicItems={epicItems}
          stones={legendaryStones}
          gold={balance?.gold ?? 0}
          selectedItemId={upgradeItemId}
          actionLoading={actionLoading}
          onSelectItem={setUpgradeItemId}
          onUpgrade={() => upgradeItemId && void legendaryUpgrade(upgradeItemId)}
        />
      )}
    </div>
  );
}

function armorGoldCost(config: BlacksmithConfig | null, level: number): number {
  if (!config) return 8000 + Math.max(0, level - 40) * 500;
  return config.armor_gold_cost_base + Math.max(0, level - 40) * config.armor_gold_cost_per_level;
}

function legendaryGoldCost(config: BlacksmithConfig | null, level: number): number {
  if (!config) return 20000 + Math.max(0, level - 40) * 800;
  return config.legendary_gold_cost_base + Math.max(0, level - 40) * config.legendary_gold_cost_per_level;
}

function shardCount(shards: MaterialStack[], level: number): number {
  return shards.find((s) => s.level === level)?.quantity ?? 0;
}

function stoneCount(stones: MaterialStack[], level: number): number {
  return stones.find((s) => s.level === level)?.quantity ?? 0;
}

function ArmorCraftPanel({
  config,
  heroLevel,
  shards,
  crypto,
  gold,
  craftSlot,
  craftLevel,
  baseItemId,
  baseCandidates,
  actionLoading,
  onSlotChange,
  onLevelChange,
  onBaseItemChange,
  onCraft,
}: {
  config: BlacksmithConfig | null;
  heroLevel: number;
  shards: MaterialStack[];
  crypto: number;
  gold: number;
  craftSlot: string;
  craftLevel: number;
  baseItemId: number | null;
  baseCandidates: PlayerItem[];
  actionLoading: boolean;
  onSlotChange: (slot: string) => void;
  onLevelChange: (level: number) => void;
  onBaseItemChange: (id: number | null) => void;
  onCraft: () => void;
}) {
  const levels = config?.craft_levels ?? [40, 50, 60, 70, 80];
  const shardsNeeded = Number(config?.shards_required[String(craftLevel)] ?? 20);
  const cryptoNeeded = Number(config?.crypto_required?.[String(craftLevel)] ?? 0);
  const prevLevel = config?.previous_level?.[String(craftLevel)];
  const goldCost = armorGoldCost(config, craftLevel);
  const haveShards = shardCount(shards, craftLevel);
  const levelLocked = heroLevel < craftLevel;
  const needsBase = prevLevel != null;
  const baseOk = !needsBase || (baseItemId != null && baseCandidates.some((i) => i.id === baseItemId));

  return (
    <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-4">
      <p className="text-gray-300 text-sm">
        Craft epic armor: MP dungeon shards, crypto (catacombs), and gold. Lv.50+ consumes the previous-tier epic in
        the same slot (unequipped, no gems).
      </p>

      <div>
        <label className="text-xs text-gray-400">Slot</label>
        <div className="flex flex-wrap gap-1 mt-1">
          {ARMOR_SLOTS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onSlotChange(s.id)}
              className={clsx(
                "px-2 py-1 rounded-lg text-xs font-bold border",
                craftSlot === s.id ? "border-kingdom-glow bg-kingdom-glow/20 text-white" : "border-gray-700 text-gray-400",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-400">Item level</label>
        <div className="flex flex-wrap gap-1 mt-1">
          {levels.map((lv) => (
            <button
              key={lv}
              type="button"
              onClick={() => onLevelChange(lv)}
              className={clsx(
                "px-2 py-1 rounded-lg text-xs font-bold border",
                craftLevel === lv ? "border-kingdom-glow bg-kingdom-glow/20 text-white" : "border-gray-700 text-gray-400",
                heroLevel < lv && "opacity-40",
              )}
            >
              Lv.{lv}
            </button>
          ))}
        </div>
      </div>

      {needsBase && (
        <div>
          <label className="text-xs text-gray-400">Base item (Lv.{prevLevel} epic, same slot)</label>
          {baseCandidates.length === 0 ? (
            <p className="text-xs text-rose-300/90 mt-1">No suitable unequipped Lv.{prevLevel} epic in this slot.</p>
          ) : (
            <select
              value={baseItemId ?? ""}
              onChange={(e) => onBaseItemChange(Number(e.target.value) || null)}
              className="mt-1 w-full bg-kingdom-dark border border-kingdom-mid rounded-lg px-3 py-2 text-sm text-white"
            >
              {baseCandidates.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.template.name}
                  {i.enhance_level > 0 ? ` +${i.enhance_level}` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="text-xs text-gray-400 space-y-1">
        <div>
          Shards Lv.{craftLevel}: {haveShards} / {shardsNeeded} required
        </div>
        <div>
          Crypto: {crypto.toLocaleString()} / {cryptoNeeded} required
        </div>
        <div>Gold: {goldCost.toLocaleString()} (have {gold.toLocaleString()})</div>
        <div>Your hero: Lv.{heroLevel}{levelLocked ? ` — need Lv.${craftLevel}` : ""}</div>
        <div>Result: Epic {ARMOR_SLOTS.find((s) => s.id === craftSlot)?.label} Lv.{craftLevel}</div>
      </div>

      <button
        type="button"
        disabled={
          actionLoading ||
          levelLocked ||
          haveShards < shardsNeeded ||
          crypto < cryptoNeeded ||
          gold < goldCost ||
          !baseOk
        }
        onClick={onCraft}
        className="w-full py-3 rounded-xl bg-amber-700 text-white font-bold disabled:opacity-40"
      >
        {actionLoading ? "Forging..." : "Craft armor"}
      </button>
    </div>
  );
}

function LegendaryUpgradePanel({
  config,
  epicItems,
  stones,
  gold,
  selectedItemId,
  actionLoading,
  onSelectItem,
  onUpgrade,
}: {
  config: BlacksmithConfig | null;
  epicItems: PlayerItem[];
  stones: MaterialStack[];
  gold: number;
  selectedItemId: number | null;
  actionLoading: boolean;
  onSelectItem: (id: number) => void;
  onUpgrade: () => void;
}) {
  const selected = useMemo(
    () => epicItems.find((i) => i.id === selectedItemId) ?? null,
    [epicItems, selectedItemId],
  );
  const level = selected?.template.item_level ?? 40;
  const stonesNeeded = Number(config?.stones_required[String(level)] ?? 2);
  const goldCost = legendaryGoldCost(config, level);
  const haveStones = stoneCount(stones, level);
  const isJewelry = selected?.template.slot.includes("RING") || selected?.template.slot.includes("NECK");

  if (epicItems.length === 0) {
    return (
      <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <h3 className="font-bold text-white">Legendary upgrade</h3>
        <p className="text-gray-400 text-sm">
          No epic gear in inventory. Craft armor from shards or buy epic jewelry in the Catacombs shop.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-4">
      <p className="text-gray-300 text-sm">
        Upgrade epic gear to legendary using MP dungeon legendary stones{isJewelry ? " and a matching shop token" : ""}.
      </p>

      <div className="max-h-48 overflow-y-auto space-y-1 rounded-lg border border-kingdom-mid bg-kingdom-dark/80 p-1">
        {epicItems.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelectItem(item.id)}
            className={clsx(
              "w-full text-left text-xs px-2 py-2 rounded-md",
              selectedItemId === item.id
                ? "bg-kingdom-mid/40 text-kingdom-glow"
                : "text-gray-300 hover:bg-kingdom-mid/30",
            )}
          >
            <span className="font-semibold">{item.template.name}</span>
            <span className="text-gray-500">
              {" "}
              · {item.template.slot} · Lv.{item.template.item_level}
            </span>
          </button>
        ))}
      </div>

      {selected && (
        <div className="text-xs text-gray-400 space-y-1">
          <div>
            Legendary stones Lv.{level}: {haveStones} / {stonesNeeded}
          </div>
          <div>Gold: {goldCost.toLocaleString()} (have {gold.toLocaleString()})</div>
          {isJewelry && (
            <div className="text-amber-200/80">
              Jewelry also requires a matching Legendary Token from the Abandoned Catacombs shop.
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        disabled={actionLoading || !selected || haveStones < stonesNeeded || gold < goldCost}
        onClick={onUpgrade}
        className="w-full py-3 rounded-xl bg-amber-700 text-white font-bold disabled:opacity-40"
      >
        {actionLoading ? "Upgrading..." : "Upgrade to legendary"}
      </button>
    </div>
  );
}
